import React, { useCallback, useRef } from "react";

/* ----------------- Tiny UI building blocks ----------------- */
export const Toggle = React.memo(({ checked, onChange }) => (
  <div
    role="switch"
    aria-checked={checked}
    onClick={(e) => {
      e.stopPropagation();
      onChange(!checked);
    }}
    style={{
      width: 34,
      height: 18,
      borderRadius: 999,
      background: checked ? "#10b981" : "#374151",
      display: "flex",
      alignItems: "center",
      justifyContent: checked ? "flex-end" : "flex-start",
      padding: 2,
      cursor: "pointer",
      transition: "background 120ms ease",
    }}
  >
    <div
      style={{ width: 12, height: 12, borderRadius: "50%", background: "#fff" }}
    />
  </div>
));

export const SectionContainer = React.memo(({ title, open, onToggle, children }) => (
  <div
    style={{
      marginBottom: 8,
      borderRadius: 8,
      background: open ? "rgba(255,255,255,0.03)" : "rgba(255,255,255,0.02)",
      border: `1px solid rgba(255,255,255,${open ? "0.06" : "0.03"})`,
      transition: "background 0.12s ease, border 0.12s ease",
    }}
  >
    <div
      onClick={onToggle}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        cursor: "pointer",
        padding: "9px 12px",
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 0.4,
        color: "#cbd5e1",
        userSelect: "none",
      }}
    >
      <span>{title}</span>
      <i className={`bi ${open ? "bi-chevron-up" : "bi-chevron-down"}`} />
    </div>
    {open && <div style={{ padding: "0 10px 10px" }}>{children}</div>}
  </div>
));

/* ----------------- Smooth slider ----------------- */
export const SmoothSlider = React.memo(({ value, onChange }) => {
  const sliderRef = useRef(null);
  const thumbRef = useRef(null);
  const isDragging = useRef(false);
  const animationFrameId = useRef(null);

  const handleDragMove = useCallback(
    (e) => {
      if (!isDragging.current || !sliderRef.current) return;
      e.preventDefault();
      cancelAnimationFrame(animationFrameId.current);
      animationFrameId.current = requestAnimationFrame(() => {
        const rect = sliderRef.current.getBoundingClientRect();
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        let newX = clientX - rect.left;
        const thumbWidth = thumbRef.current ? thumbRef.current.offsetWidth : 14;
        const maxX = rect.width - thumbWidth / 2;
        const minX = -thumbWidth / 2;
        newX = Math.max(minX, Math.min(newX, maxX));
        const newValue = (newX - minX) / (maxX - minX);
        onChange(Number(newValue.toFixed(2)));
      });
    },
    [onChange]
  );

  const handleDragEnd = useCallback(() => {
    isDragging.current = false;
    window.removeEventListener("mousemove", handleDragMove);
    window.removeEventListener("mouseup", handleDragEnd);
    window.removeEventListener("touchmove", handleDragMove);
    window.removeEventListener("touchend", handleDragEnd);
    cancelAnimationFrame(animationFrameId.current);
  }, [handleDragMove]);

  const handleDragStart = useCallback(
    (e) => {
      isDragging.current = true;
      window.addEventListener("mousemove", handleDragMove, { passive: false });
      window.addEventListener("mouseup", handleDragEnd);
      window.addEventListener("touchmove", handleDragMove, { passive: false });
      window.addEventListener("touchend", handleDragEnd);
      handleDragMove(e);
    },
    [handleDragMove, handleDragEnd]
  );

  const displayValue = value ?? 1;

  return (
    <div style={{ flex: 1, display: "flex", alignItems: "center", height: 28 }}>
      <div
        ref={sliderRef}
        onMouseDown={handleDragStart}
        onTouchStart={handleDragStart}
        style={{
          width: "100%",
          height: 6,
          borderRadius: 3,
          position: "relative",
          background: `linear-gradient(90deg, #10b981 ${
            displayValue * 100
          }%, #374151 ${displayValue * 100}%)`,
          cursor: "pointer",
        }}
      >
        <div
          ref={thumbRef}
          style={{
            position: "absolute",
            top: "50%",
            left: `${displayValue * 100}%`,
            width: 14,
            height: 14,
            borderRadius: "50%",
            background: "#fff",
            border: "2px solid #10b981",
            transform: "translate(-50%, -50%)",
            pointerEvents: "none",
            userSelect: "none",
            zIndex: 2,
          }}
        />
        <div
          style={{
            position: "absolute",
            top: "-18px",
            left: `${displayValue * 100}%`,
            transform: "translate(-60%, 0)",
            fontSize: 10,
            color: "#9ca3af",
            userSelect: "none",
            pointerEvents: "none",
            zIndex: 3,
            minWidth: 28,
            textAlign: "center",
          }}
        >
          {Math.round(displayValue * 100)}%
        </div>
      </div>
    </div>
  );
});

/* ----------------- Layer card ----------------- */
export const LayerCard = React.memo(
  ({ bucket, layer, onToggle, onOpacityChange, onRemove, onZoom }) => (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 8,
        padding: "8px 10px",
        borderRadius: 6,
        background: layer.visible
          ? "rgba(16,185,129,0.06)"
          : "rgba(255,255,255,0.02)",
        border: "1px solid rgba(255,255,255,0.04)",
        fontSize: 12,
        marginBottom: 6,
      }}
    >
      <div style={{ flex: "1 1 auto" }}>
        <div style={{ fontWeight: 600, color: "#f3f4f6", fontSize: 12 }}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (typeof onZoom === "function") onZoom(layer);
            }}
            title="Zoom to layer"
            style={{
              background: "transparent",
              border: "none",
              color: "inherit",
              fontWeight: 600,
              fontSize: 12,
              cursor: "pointer",
              padding: 0,
              textAlign: "left",
            }}
          >
            {layer.title}
          </button>
        </div>

        {typeof layer.count === "number" && (
          <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>
            {layer.count} features
          </div>
        )}

        {bucket.includes("raster") && (
          <div
            style={{
              marginTop: 8,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <div style={{ fontSize: 11, color: "#9ca3af", minWidth: 58 }}>
              Opacity
            </div>
            <SmoothSlider
              value={layer.opacity ?? 1}
              onChange={onOpacityChange}
            />
          </div>
        )}
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 6,
        }}
      >
        <Toggle
          checked={!!layer.visible}
          onChange={() => onToggle && onToggle(layer)}
        />

        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (typeof onRemove === "function") {
              try {
                onRemove(layer);
              } catch (err) {
                console.error("[LayerCard] onRemove threw:", err);
              }
            }
          }}
          title="Remove"
          style={{
            background: "transparent",
            border: "none",
            color: "#94a3b8",
            cursor: "pointer",
            padding: 4,
            borderRadius: 6,
            fontSize: 13,
          }}
        >
          <i className="bi bi-trash" />
        </button>
      </div>
    </div>
  )
);